import { useState, useEffect, useMemo } from "react";
import { wpOffersAPI, reonicAPI } from "../lib/api";
import { toast } from "sonner";
import { TrendingDown, Loader2, Sparkles, Calculator, Leaf } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";

const DEFAULTS = {
  jaz: 4.2,
  electricity_price_eur_kwh: 0.30,
  current_energy_price_eur_kwh: 0.12,  // Gas
  subsidy_eur: 0,
  expected_lifetime_years: 20,
  co2_factor_current_kg_kwh: 0.201,    // Erdgas
  co2_factor_electricity_kg_kwh: 0.401, // DE-Strommix 2025
};

const HEATING_TYPES = [
  { value: "Gas", label: "Erdgas", co2: 0.201, defaultPrice: 0.12 },
  { value: "Öl", label: "Heizöl", co2: 0.266, defaultPrice: 0.11 },
  { value: "Strom", label: "Strom (Direkt)", co2: 0.401, defaultPrice: 0.32 },
  { value: "Holz", label: "Holz / Pellets", co2: 0.04, defaultPrice: 0.075 },
];

/**
 * Wirtschaftlichkeitskarte für WP-Angebote.
 * 
 * Beispiel-Snippet für die Reonic-Vorbelegung:
 *   const handleLoadFromReonic = async () => {
 *     const res = await reonicAPI.previewOffer(offer.reonic_offer_id);
 *     const wp = res.data.wp_offer || {};
 *     const hp = wp.hp_specifics || {};
 *     setForm(prev => ({
 *       ...prev,
 *       heat_demand_kwh: hp.heat_demand_kwh ?? prev.heat_demand_kwh,
 *       current_heating_type: hp.current_heating_type ?? prev.current_heating_type,
 *       current_energy_cost_eur: hp.current_energy_cost_eur ?? prev.current_energy_cost_eur,
 *     }));
 *   };
 */
export default function WPProfitabilityCard({ offer, onUpdated }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingReonic, setLoadingReonic] = useState(false);
  const [form, setForm] = useState({
    jaz: DEFAULTS.jaz,
    heat_demand_kwh: offer?.heat_demand_kwh ?? "",
    electricity_price_eur_kwh: DEFAULTS.electricity_price_eur_kwh,
    current_energy_consumption_kwh: "",
    current_energy_price_eur_kwh: DEFAULTS.current_energy_price_eur_kwh,
    current_energy_cost_eur: offer?.current_energy_cost_eur ?? "",
    current_heating_type: offer?.current_heating_type ?? "Gas",
    hp_investment_eur: offer?.subtotal ?? "",
    subsidy_eur: DEFAULTS.subsidy_eur,
    expected_lifetime_years: DEFAULTS.expected_lifetime_years,
    co2_factor_current_kg_kwh: DEFAULTS.co2_factor_current_kg_kwh,
    co2_factor_electricity_kg_kwh: DEFAULTS.co2_factor_electricity_kg_kwh,
  });
  const [results, setResults] = useState(null);

  // Initial: gespeicherte Werte + Defaults laden
  useEffect(() => {
    const init = async () => {
      try {
        const res = await wpOffersAPI.getProfitability(offer.id);
        const saved = res.data.profitability;
        const defaults = res.data.defaults || {};
        if (saved) {
          setForm({ ...form, ...saved });
          setResults(saved);
        } else {
          setForm((f) => ({
            ...f,
            heat_demand_kwh: defaults.heat_demand_kwh ?? f.heat_demand_kwh,
            current_energy_cost_eur: defaults.current_energy_cost_eur ?? f.current_energy_cost_eur,
            current_heating_type: defaults.current_heating_type ?? f.current_heating_type,
            hp_investment_eur: defaults.hp_investment_eur ?? f.hp_investment_eur,
          }));
        }
      } catch (err) {
        // keine Vorbelegung → User füllt manuell
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.id]);

  // Heizart wechseln → CO₂-Faktor + Default-Preis nachziehen
  const handleHeatingTypeChange = (value) => {
    const t = HEATING_TYPES.find((x) => x.value === value);
    setForm((f) => ({
      ...f,
      current_heating_type: value,
      co2_factor_current_kg_kwh: t?.co2 ?? f.co2_factor_current_kg_kwh,
      current_energy_price_eur_kwh: t?.defaultPrice ?? f.current_energy_price_eur_kwh,
    }));
  };

  // === Auto-Fill aus Reonic-Daten ===
  const handleLoadFromReonic = async () => {
    if (!offer.reonic_offer_id) {
      toast.error("Dieses Angebot stammt nicht aus einem Reonic-Import");
      return;
    }
    setLoadingReonic(true);
    try {
      const res = await reonicAPI.preview(offer.reonic_offer_id);
      const data = res.data || {};
      const wp = data.wp_offer || {};
      const hp = wp.hp_specifics || {};
      const shared = data.shared || {};
      // Werte aus Reonic-Preview übernehmen (nur, wo Reonic was liefert)
      setForm((prev) => ({
        ...prev,
        heat_demand_kwh: hp.heat_demand_kwh ?? prev.heat_demand_kwh,
        current_heating_type: hp.current_heating_type ?? prev.current_heating_type,
        current_energy_cost_eur: hp.current_energy_cost_eur ?? prev.current_energy_cost_eur,
        // Fallback: wenn Reonic Verbrauch via heat_demand + JAZ rückrechnen will, gibt es das aktuell nicht
        // → wir lassen current_energy_consumption_kwh leer, User trägt es ein
      }));
      toast.success("Reonic-Werte übernommen — bitte JAZ und Strompreis prüfen.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reonic-Daten konnten nicht geladen werden");
    } finally {
      setLoadingReonic(false);
    }
  };

  const num = (v) => {
    const x = Number.parseFloat(v);
    return Number.isFinite(x) ? x : null;
  };

  // Live-Berechnung (clientseitig — Backend ist Source of Truth beim Speichern)
  const liveCalc = useMemo(() => {
    const jaz = num(form.jaz);
    const heatDemand = num(form.heat_demand_kwh);
    const elecPrice = num(form.electricity_price_eur_kwh);
    let currentCost = num(form.current_energy_cost_eur);
    const consKwh = num(form.current_energy_consumption_kwh);
    const consPrice = num(form.current_energy_price_eur_kwh);
    if (currentCost == null && consKwh != null && consPrice != null) {
      currentCost = consKwh * consPrice;
    }
    const invest = num(form.hp_investment_eur);
    const subsidy = num(form.subsidy_eur) ?? 0;
    const lifetime = num(form.expected_lifetime_years) ?? 20;
    const co2Old = num(form.co2_factor_current_kg_kwh) ?? 0;
    const co2New = num(form.co2_factor_electricity_kg_kwh) ?? 0;

    const hpKwh = (heatDemand && jaz && jaz > 0) ? Math.round(heatDemand / jaz) : null;
    const hpCost = (hpKwh != null && elecPrice != null) ? +(hpKwh * elecPrice).toFixed(2) : null;
    const savings = (currentCost != null && hpCost != null) ? +(currentCost - hpCost).toFixed(2) : null;
    const payback = (savings && savings > 0 && invest != null)
      ? +(((invest - subsidy) > 0 ? (invest - subsidy) / savings : 0).toFixed(1))
      : null;
    const lifetimeSavings = (savings != null && invest != null)
      ? +(savings * lifetime - (invest - subsidy)).toFixed(2)
      : null;
    const co2Savings = (consKwh != null && hpKwh != null)
      ? Math.round(consKwh * co2Old - hpKwh * co2New)
      : null;

    return { hpKwh, hpCost, savings, payback, lifetimeSavings, co2Savings, currentCost };
  }, [form]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null) return;
        const n = Number.parseFloat(v);
        payload[k] = Number.isFinite(n) && typeof v !== "string" ? n :
                     (typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v) ? n : v);
      });
      const res = await wpOffersAPI.upsertProfitability(offer.id, payload);
      setResults(res.data);
      toast.success("Wirtschaftlichkeit gespeichert. PDF enthält jetzt die Kennzahlen.");
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n, suffix = "") => (n == null ? "—" : `${Number(n).toLocaleString("de-DE", { maximumFractionDigits: 2 })} ${suffix}`.trim());

  if (loading) return null;

  return (
    <Card className="border-2 border-emerald-300 bg-emerald-50/40" data-testid="wp-profitability-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-emerald-600" />
            Wirtschaftlichkeit
            {results && <Badge className="bg-emerald-600 text-xs">gespeichert</Badge>}
          </span>
          {offer.reonic_offer_id && (
            <Button
              variant="outline" size="sm"
              onClick={handleLoadFromReonic}
              disabled={loadingReonic}
              data-testid="load-from-reonic-btn"
            >
              {loadingReonic ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              Aus Reonic laden
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Live-Kennzahlen */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Metric label="HP-Stromverbrauch" value={fmt(liveCalc.hpKwh, "kWh")} testid="metric-hp-kwh" />
          <Metric label="HP-Jahreskosten" value={fmt(liveCalc.hpCost, "€")} testid="metric-hp-cost" />
          <Metric
            label="Jährliche Einsparung"
            value={fmt(liveCalc.savings, "€")}
            color={liveCalc.savings > 0 ? "emerald" : "red"}
            testid="metric-savings"
          />
          <Metric
            label="Amortisation"
            value={fmt(liveCalc.payback, "Jahre")}
            color="emerald"
            testid="metric-payback"
          />
        </div>

        {(liveCalc.lifetimeSavings != null || liveCalc.co2Savings != null) && (
          <div className="flex flex-wrap gap-4 text-sm bg-white/60 rounded-lg p-3 border border-emerald-200">
            {liveCalc.lifetimeSavings != null && (
              <div className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-emerald-600" />
                <span>Gesamtersparnis ({form.expected_lifetime_years} J): <strong>{fmt(liveCalc.lifetimeSavings, "€")}</strong></span>
              </div>
            )}
            {liveCalc.co2Savings != null && (
              <div className="flex items-center gap-2">
                <Leaf className="h-4 w-4 text-emerald-600" />
                <span>CO₂-Einsparung: <strong>{fmt(liveCalc.co2Savings, "kg/Jahr")}</strong></span>
              </div>
            )}
          </div>
        )}

        {results?.warnings?.length > 0 && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertDescription className="text-sm text-amber-900">
              {results.warnings.map((w, i) => <div key={i}>• {w}</div>)}
            </AlertDescription>
          </Alert>
        )}

        {/* Eingabe-Formular */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Jahresarbeitszahl (JAZ)" id="jaz">
            <Input
              type="number" step="0.1" min="1" max="6"
              value={form.jaz}
              onChange={(e) => setForm({ ...form, jaz: e.target.value })}
              data-testid="input-jaz"
            />
          </Field>
          <Field label="Strompreis WP (€/kWh)" id="elec">
            <Input
              type="number" step="0.01" min="0"
              value={form.electricity_price_eur_kwh}
              onChange={(e) => setForm({ ...form, electricity_price_eur_kwh: e.target.value })}
              data-testid="input-electricity-price"
            />
          </Field>
          <Field label="Wärmebedarf (kWh/Jahr)" id="hd" hint="Aus Reonic-Import oder Heizlast-Berechnung">
            <Input
              type="number" min="0"
              value={form.heat_demand_kwh}
              onChange={(e) => setForm({ ...form, heat_demand_kwh: e.target.value })}
              data-testid="input-heat-demand"
            />
          </Field>
          <Field label="Bisherige Heizart" id="heat">
            <Select value={form.current_heating_type} onValueChange={handleHeatingTypeChange}>
              <SelectTrigger data-testid="select-heating-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HEATING_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Altverbrauch (kWh/Jahr)" id="cons">
            <Input
              type="number" min="0"
              value={form.current_energy_consumption_kwh}
              onChange={(e) => setForm({ ...form, current_energy_consumption_kwh: e.target.value })}
              data-testid="input-current-consumption"
            />
          </Field>
          <Field label="Alt-Energiepreis (€/kWh)" id="oldprice">
            <Input
              type="number" step="0.01" min="0"
              value={form.current_energy_price_eur_kwh}
              onChange={(e) => setForm({ ...form, current_energy_price_eur_kwh: e.target.value })}
              data-testid="input-current-price"
            />
          </Field>
          <Field label="Bisherige Heizkosten (€/Jahr)" id="oldcost" hint="Override; sonst aus Verbrauch × Preis">
            <Input
              type="number" min="0"
              value={form.current_energy_cost_eur}
              onChange={(e) => setForm({ ...form, current_energy_cost_eur: e.target.value })}
              data-testid="input-current-cost"
            />
          </Field>
          <Field label="Investition WP-Netto (€)" id="inv" hint={`Default: ${(offer.subtotal || 0).toFixed(2)} €`}>
            <Input
              type="number" min="0"
              value={form.hp_investment_eur}
              onChange={(e) => setForm({ ...form, hp_investment_eur: e.target.value })}
              data-testid="input-investment"
            />
          </Field>
          <Field label="BAFA-Förderung (€)" id="bafa">
            <Input
              type="number" min="0"
              value={form.subsidy_eur}
              onChange={(e) => setForm({ ...form, subsidy_eur: e.target.value })}
              data-testid="input-subsidy"
            />
          </Field>
          <Field label="Lebensdauer (Jahre)" id="life">
            <Input
              type="number" min="5" max="40"
              value={form.expected_lifetime_years}
              onChange={(e) => setForm({ ...form, expected_lifetime_years: e.target.value })}
              data-testid="input-lifetime"
            />
          </Field>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving} data-testid="save-profitability-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Speichern & PDF freischalten
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, id, hint, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs">{label}</Label>
      {children}
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

function Metric({ label, value, color, testid }) {
  const colorCls = color === "emerald" ? "text-emerald-700" : color === "red" ? "text-red-700" : "text-foreground";
  return (
    <div className="bg-white rounded-lg p-3 border border-emerald-200">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold ${colorCls}`} data-testid={testid}>{value}</div>
    </div>
  );
}
